import { useEffect } from "react";
import { Drawer } from "vaul";

import { acquireCKEditorBodyFlag } from "@/shared/lib/ckEditorBodyFlag";
import { isCKEditorElement, toElement } from "@/shared/lib/isCKEditorElement";

type DrawerContentProps = React.ComponentProps<typeof Drawer.Content>;
type OutsideInteractionEvent = Parameters<NonNullable<DrawerContentProps["onInteractOutside"]>>[0];
type CKEditorContentProps = Pick<DrawerContentProps, "onPointerDownOutside" | "onInteractOutside">;

/**
 * vaul always mounts Radix's modal Dialog, whose FocusScope + DismissableLayer
 * hijack focus and dismissal for CKEditor panels portaled to `document.body`
 * (outside the drawer). This neutralizes both — for `.ck-*` targets only — so
 * toolbar sub-menus stay open. Opt-in; leaves every other drawer untouched.
 */
export const useCKEditorDrawerCompat = (open: boolean, enabled = false) => {
  useEffect(() => {
    if (!open || !enabled) return;

    const releaseBodyFlag = acquireCKEditorBodyFlag();

    const stopFocusTrapForCKEditor = (event: FocusEvent) => {
      if (
        isCKEditorElement(toElement(event.target))
        || isCKEditorElement(toElement(event.relatedTarget))
      ) {
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener("focusin", stopFocusTrapForCKEditor, true);
    document.addEventListener("focusout", stopFocusTrapForCKEditor, true);

    return () => {
      document.removeEventListener("focusin", stopFocusTrapForCKEditor, true);
      document.removeEventListener("focusout", stopFocusTrapForCKEditor, true);
      releaseBodyFlag();
    };
  }, [open, enabled]);

  const preventDismissOnCKEditor = (event: OutsideInteractionEvent) => {
    if (isCKEditorElement(toElement(event.detail.originalEvent.target))) {
      event.preventDefault();
    }
  };

  const ckEditorContentProps: CKEditorContentProps = enabled
    ? { onPointerDownOutside: preventDismissOnCKEditor, onInteractOutside: preventDismissOnCKEditor }
    : {};

  return { ckEditorContentProps };
};
